/* eslint-disable react-refresh/only-export-components -- shared tab primitives intentionally export components and descriptor helpers. */
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { type ReactNode } from 'react'
import type { NlsKey } from '../../../nls/strings_nls'
import type { AnalysisRecommendation, AnalysisSnapshot, AnalysisTabKey, AnalysisTimeRangeKey, AnalysisTrend } from '../../../types/analysis'
import { Button } from '../../Common/Button'
import { RailPageShellCard } from '../../Common/RailPageShell'
import { StatusChip as CommonStatusChip } from '../../Common'
import { Chart } from '../../Common/Chart/Chart.js'
import type { ChartResult } from '../../../types/chart.js'
import { createAnalysisChartSpec } from '../popover/analysisChartMapping.js'
import { AnalysisScoreRing } from '../AnalysisScoreRing'
import type { ChartDescriptor, CompositionDonutData, MultiSeriesAreaData } from '../charts'
import { usePoppableCard } from '../popover/usePoppableCard'
import { useNls } from '../../../nls/nlsProvider.js'

import { buildTrendDescriptor } from '../popover/descriptors'

export type AnalysisNavigateTarget = AnalysisRecommendation['drillTarget'] | 'REPORTS'
export type AnalysisNavigate = (target: AnalysisNavigateTarget) => void

export type TabDefinition = {
  key: AnalysisTabKey
  label: string
  subtitle: string
  icon: ReactNode
  fullOnly?: boolean
}

export const TIME_RANGES: Array<{
  key: AnalysisTimeRangeKey
  labelKey: NlsKey
}> = [
  { key: '24H', labelKey: 'NLSAnalysisTimeRange24H' },
  { key: '7D', labelKey: 'NLSAnalysisTimeRange7D' },
  { key: '30D', labelKey: 'NLSAnalysisTimeRange30D' },
  { key: '90D', labelKey: 'NLSAnalysisTimeRange90D' },
  { key: '12M', labelKey: 'NLSAnalysisTimeRange12M' },
]

export const DRILL_COPY: Record<AnalysisRecommendation['drillTarget'], string> = {
  CHECKLISTS: 'Open Checklists',
  MEETINGS: 'Open Meetings',
  UPLOAD: 'Open Uploads',
  LINK: 'Open Link Hub',
  AUTOMATE: 'Open Automate',
  BUDGET: 'Open Budget',
  STOCK: 'Open Stock',
  SUMMARY: 'Open Ledger',
  REPORTS: 'Open Reports',
  DASHBOARD: 'Open Dashboard',
  AI_MAGIC: 'Open AI Magic',
}

export const formatNumber = (value: number) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value)

export const formatPercent = (value: number) => `${Math.round(value)}%`

export const SUB_SCORE_LABELS: Record<keyof AnalysisSnapshot['health']['subScores'], string> = {
  execution: 'Execution',
  blocker: 'Blocker',
  drift: 'Drift',
  ops: 'Ops',
  ai: 'AI',
}

export const MAIN_CHART_SERIES: Array<{
  id: string
  label: string
  accent: string
}> = [
  { id: 'completion', label: 'Completion', accent: 'var(--chart-2)' },
  { id: 'overdue', label: 'Overdue', accent: 'var(--chart-4)' },
  { id: 'blocked', label: 'Blocked', accent: 'var(--chart-3)' },
  { id: 'activity', label: 'Activity', accent: 'var(--chart-6)' },
]

export const COMPOSITION_COLORS_HEX: Record<'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST', string> = {
  GOAL: 'var(--chart-1)',
  TASK: 'var(--chart-6)',
  SUBTASK: 'var(--chart-2)',
  CHECKLIST: 'var(--chart-3)',
}

export const COMPOSITION_COLORS: Record<'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST', string> = {
  GOAL: 'var(--chart-1)',
  TASK: 'var(--chart-6)',
  SUBTASK: 'var(--chart-2)',
  CHECKLIST: 'var(--chart-3)',
}

export const Sparkline = ({ trend }: { trend: AnalysisTrend }) => {
  const { t } = useNls()
  const width = 160
  const height = 56
  const points = trend.points.length ? trend.points : [0]
  const isFlat = points.every((point) => point === 0)
  const spec = createAnalysisChartSpec({
    id: `sparkline-${trend.id}`,
    title: trend.label || t('NLSAnalysisTrend'),
    type: 'line',
    style: {
      legend: 'off',
      seriesColor: {
        trend: isFlat ? 'var(--border)' : 'var(--chart-6)',
      },
    },
  })
  const data: ChartResult = {
    series: [
      {
        key: 'trend',
        label: trend.label,
        points: points.map((val, idx) => ({ x: `t${idx}`, y: val })),
      },
    ],
    asOf: new Date().toISOString(),
  }
  return (
    <div className={`analysisSparkline${isFlat ? ' isFlat' : ''}`} role="img" aria-label={`${trend.label} trend`}>
      <Chart spec={spec} data={data} width={width} height={height} />
    </div>
  )
}

export const buildMultiSeriesDescriptor = ({ snapshot, onOpenReports }: { snapshot: AnalysisSnapshot; onOpenReports: () => void }): ChartDescriptor<MultiSeriesAreaData> => {
  const labels = snapshot.trends[0]?.points.map((_, index) => `t-${snapshot.trends[0].points.length - index}`) ?? []
  const series = MAIN_CHART_SERIES.map((meta) => {
    const trend = snapshot.trends.find((candidate) => candidate.id === meta.id)
    return trend
      ? {
          id: meta.id,
          label: meta.label,
          color: meta.accent,
          points: trend.points.map((value, index) => ({
            label: labels[index] ?? `t${index}`,
            value,
          })),
        }
      : null
  }).filter((entry): entry is NonNullable<typeof entry> => entry !== null && entry.points.some((point) => point.value !== 0))
  return {
    id: `multi-series-${snapshot.workspaceId}`,
    kind: 'multi-series-area',
    title: 'Signals over time',
    subtitle: `${series.length} live series`,
    source: { tab: 'OVERVIEW', cardLabel: 'Signals over time' },
    data: { series },
    buildRecords: () =>
      series.map((entry) => ({
        id: entry.id,
        primary: entry.label,
        secondary: `Current: ${entry.points.at(-1)?.value ?? 0}`,
        numeric: entry.points.at(-1)?.value ?? 0,
        drillTarget: entry.id === 'overdue' ? 'DASHBOARD' : entry.id === 'blocked' ? 'CHECKLISTS' : null,
      })),
    onOpenReports,
  }
}

export const buildCompositionDescriptor = ({ snapshot, onOpenReports }: { snapshot: AnalysisSnapshot; onOpenReports: () => void }): ChartDescriptor<CompositionDonutData> => {
  const segments = (Object.entries(snapshot.work.byType) as Array<[keyof typeof snapshot.work.byType, number]>)
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({
      id: key,
      label: key === 'CHECKLIST' ? 'Checklist' : key === 'GOAL' ? 'Goals' : key === 'TASK' ? 'Tasks' : 'Subtasks',
      value,
      color: COMPOSITION_COLORS_HEX[key],
    }))
  return {
    id: `composition-${snapshot.workspaceId}`,
    kind: 'composition-donut',
    title: 'Work composition',
    subtitle: `${snapshot.work.total} records`,
    source: { tab: 'OVERVIEW', cardLabel: 'Composition' },
    data: { segments },
    buildRecords: () =>
      segments.map((segment) => ({
        id: segment.id,
        primary: segment.label,
        secondary: `${Math.round((segment.value / Math.max(1, snapshot.work.total)) * 100)}% of total`,
        numeric: segment.value,
        drillTarget: 'DASHBOARD',
      })),
    onOpenReports,
  }
}

export const MultiSeriesChart = ({ trends, descriptor }: { trends: AnalysisTrend[]; descriptor?: ChartDescriptor | null }) => {
  const { t } = useNls()
  const { cardProps } = usePoppableCard(descriptor)
  const height = 220
  const series = MAIN_CHART_SERIES.map((meta) => ({
    ...meta,
    trend: trends.find((trend) => trend.id === meta.id),
  })).flatMap((entry) =>
    entry.trend && entry.trend.points.length > 0
      ? [
          {
            id: entry.id,
            label: entry.label,
            accent: entry.accent,
            trend: entry.trend,
          },
        ]
      : [],
  )
  if (series.length === 0) {
    return (
      <RailPageShellCard {...cardProps} className={`analysisMainChart analysisMainChart--empty${cardProps.className ? ` ${cardProps.className}` : ''}`}>
        <div className="analysisChartHeader">
          <span className="settingsEyebrow">Signals over time</span>
          <h3>No local trend data yet</h3>
          <p>Trend series populate as work is created, completed, blocked, or updated across the window.</p>
        </div>
      </RailPageShellCard>
    )
  }

  const spec = createAnalysisChartSpec({
    id: 'analysis-signals-over-time',
    title: descriptor?.title || t('NLSAnalysisSignalsOverTime'),
    type: 'area',
    style: {
      legend: 'off',
      seriesColor: Object.fromEntries(MAIN_CHART_SERIES.map((s) => [s.id, s.accent])),
    },
  })
  const chartResult: ChartResult = {
    series: series.map((s) => ({
      key: s.id,
      label: s.label,
      points: s.trend.points.map((val, idx) => ({
        x: idx === 0 ? 'earlier' : idx === s.trend.points.length - 1 ? 'now' : `t${idx}`,
        y: val,
      })),
    })),
    asOf: new Date().toISOString(),
  }

  return (
    <RailPageShellCard {...cardProps} className={`analysisMainChart${cardProps.className ? ` ${cardProps.className}` : ''}`}>
      <div className="analysisChartHeader">
        <div>
          <span className="settingsEyebrow">Signals over time</span>
          <h3>Trend momentum</h3>
          <p>Overlaid movement across completion, overdue, blocked, and activity signals in the selected window.</p>
        </div>
        <ul className="analysisChartLegend" aria-hidden="true">
          {series.map((entry) => (
            <li key={entry.id}>
              <i style={{ background: entry.accent }} />
              {entry.label} <em>{formatNumber(entry.trend.current)}</em>
            </li>
          ))}
        </ul>
      </div>
      <div className="analysisMainChart__svg">
        <Chart spec={spec} data={chartResult} height={height} />
      </div>
    </RailPageShellCard>
  )
}

export const CompositionDonut = ({ byType, total, descriptor }: { byType: AnalysisSnapshot['work']['byType']; total: number; descriptor?: ChartDescriptor | null }) => {
  const { t } = useNls()
  const { cardProps } = usePoppableCard(descriptor)
  const entries = (Object.entries(byType) as Array<[keyof typeof byType, number]>).filter(([, value]) => value > 0).sort((a, b) => b[1] - a[1])
  const size = 132

  const spec = createAnalysisChartSpec({
    id: 'analysis-composition-donut',
    title: descriptor?.title || t('NLSAnalysisComposition'),
    type: 'donut',
    style: {
      legend: 'off',
      innerRadius: 46,
      seriesColor: Object.fromEntries(entries.map(([key]) => [key, COMPOSITION_COLORS[key]])),
    },
  })
  const chartResult: ChartResult = {
    series: [
      {
        key: 'composition',
        label: 'Work composition',
        points: entries.map(([key, value]) => ({
          x: key === 'CHECKLIST' ? 'Checklist' : key === 'GOAL' ? 'Goals' : key === 'TASK' ? 'Tasks' : 'Subtasks',
          y: value,
        })),
      },
    ],
    total: total,
    asOf: new Date().toISOString(),
  }

  return (
    <RailPageShellCard {...cardProps} className={`analysisComposition${cardProps.className ? ` ${cardProps.className}` : ''}`}>
      <header>
        <span className="settingsEyebrow">Work composition</span>
        <h3>{formatNumber(total)} records</h3>
      </header>
      {entries.length === 0 ? (
        <p className="analysisCompositionEmpty">No workspace records in this window.</p>
      ) : (
        <div className="analysisComposition__body">
          <div className="analysisComposition__ring" role="img" aria-label="Composition donut">
            <Chart spec={spec} data={chartResult} width={size} height={size} />
            <strong>{formatNumber(total)}</strong>
            <span>total</span>
          </div>
          <ul className="analysisComposition__legend">
            {entries.map(([key, value]) => (
              <li key={key}>
                <i style={{ background: COMPOSITION_COLORS[key] }} />
                <span>{key === 'CHECKLIST' ? 'Checklist' : key === 'GOAL' ? 'Goals' : key === 'TASK' ? 'Tasks' : 'Subtasks'}</span>
                <em>{formatNumber(value)}</em>
              </li>
            ))}
          </ul>
        </div>
      )}
    </RailPageShellCard>
  )
}

export const trendDeltaTone = (trend: AnalysisTrend) => {
  if (trend.delta === 0) return ''
  const increaseIsGood = trend.direction === 'up-is-good'
  return trend.delta > 0 === increaseIsGood ? 'isGood' : 'isBad'
}

export const TrendCard = ({ trend, descriptor }: { trend: AnalysisTrend; descriptor?: ChartDescriptor | null }) => {
  const { cardProps } = usePoppableCard(descriptor)
  const isEmpty = trend.current === 0 && trend.delta === 0 && trend.points.every((point) => point === 0)
  return (
    <RailPageShellCard {...cardProps} className={`analysisTrendCard${isEmpty ? ' analysisTrendCard--empty' : ''}${cardProps.className ? ` ${cardProps.className}` : ''}`}>
      <header>
        <span>{trend.label}</span>
        <strong>{formatNumber(trend.current)}</strong>
      </header>
      <Sparkline trend={trend} />
      <small className={trendDeltaTone(trend)}>
        {isEmpty ? (
          <span className="analysisTrendMuted">— no movement</span>
        ) : (
          <>
            <span aria-hidden="true">{trend.delta > 0 ? '▲' : trend.delta < 0 ? '▼' : '·'}</span>
            {trend.delta > 0 ? '+' : ''}
            {formatNumber(trend.delta)} vs prior bucket
          </>
        )}
      </small>
    </RailPageShellCard>
  )
}

export const TrendStrip = ({ trends, snapshot, onOpenReports }: { trends: AnalysisTrend[]; snapshot?: AnalysisSnapshot; onOpenReports?: () => void }) => {
  if (!trends.length) return null
  return (
    <div className="analysisTrendBlock">
      <header className="analysisTrendBlock__header">
        <span className="settingsEyebrow">Trend strip</span>
        <h3>Signal cards</h3>
      </header>
      <div className="analysisTrendStrip">
        {trends.map((trend) => {
          const descriptor = snapshot && onOpenReports ? buildTrendDescriptor({ snapshot, trend, onOpenReports }) : null
          return <TrendCard key={trend.id} trend={trend} descriptor={descriptor} />
        })}
      </div>
    </div>
  )
}

export const ScoreRing = ({ snapshot, descriptor, memberDense = false, healthComponentsLabel, scoreScaleLabel }: { snapshot: AnalysisSnapshot; descriptor?: ChartDescriptor | null; memberDense?: boolean; healthComponentsLabel?: ReactNode; scoreScaleLabel?: ReactNode }) => {
  const { cardProps } = usePoppableCard(descriptor)
  const subScores = (
    <div className="analysisSubScores">
      {memberDense && healthComponentsLabel ? <span className="analysisSubScores__heading">{healthComponentsLabel}</span> : null}
      {(Object.entries(snapshot.health.subScores) as Array<[keyof AnalysisSnapshot['health']['subScores'], number]>).map(([label, value]) => (
        <div className="analysisSubScore" key={label}>
          <span>{SUB_SCORE_LABELS[label]}</span>
          <em>{formatPercent(value)}</em>
          <i style={{ width: `${value}%` }} />
        </div>
      ))}
    </div>
  )
  return (
    <RailPageShellCard {...cardProps} className={`analysisScore analysisScore--${snapshot.health.state.toLowerCase()}${cardProps.className ? ` ${cardProps.className}` : ''}`}>
      <AnalysisScoreRing score={snapshot.health.score} state={snapshot.health.state} compact={memberDense} compactScaleLabel={scoreScaleLabel} />
      <div className="analysisScore__body">
        <span className="settingsEyebrow">Workspace health</span>
        <h2>{snapshot.health.state === 'HEALTHY' ? 'Stable operating picture' : snapshot.health.state === 'WATCH' ? 'Attention recommended' : 'Critical pressure detected'}</h2>
        <p>
          {formatNumber(snapshot.work.active)} active items, {formatNumber(snapshot.work.blocked)} blocked, {formatNumber(snapshot.work.overdue)} overdue.
        </p>
        {!memberDense ? subScores : null}
      </div>
      {memberDense ? subScores : null}
    </RailPageShellCard>
  )
}

export const StatusChip = ({ children, title }: { children: ReactNode; title?: string }) => (
  <span title={title}>
    <CommonStatusChip tone="info" size="sm" icon={<span aria-hidden="true" />}>
      {children}
    </CommonStatusChip>
  </span>
)

export const AiSummaryHero = ({ snapshot, summary }: { snapshot: AnalysisSnapshot; summary: string | null }) => (
  <RailPageShellCard className="analysisAiHero">
    <header>
      <AutoAwesomeRoundedIcon />
      <div>
        <span className="settingsEyebrow">AI operational summary</span>
        <h2>What should happen next</h2>
      </div>
    </header>
    <p>
      {summary ??
        `Local analysis sees ${formatNumber(snapshot.work.blocked)} blocker signals, ${formatNumber(snapshot.work.overdue)} overdue items, and ${formatNumber(snapshot.ops.proofRequired)} proof gates in the current view. ${formatNumber(snapshot.governance.recentlyUpdated)} item${snapshot.governance.recentlyUpdated === 1 ? '' : 's'} changed in the last day, and the next recommended move is ${snapshot.recommendations[0]?.title.toLowerCase() ?? 'keeping the operating rhythm'}.`}
    </p>
  </RailPageShellCard>
)

export const RecommendationsFooter = ({ recommendations, onNavigate }: { recommendations: AnalysisRecommendation[]; onNavigate: AnalysisNavigate }) => (
  <div className="analysisRecommendations">
    <header>
      <h2>Recommended next actions</h2>
      <span>{recommendations.length} ranked</span>
    </header>
    <div>
      {recommendations.map((recommendation) => (
        <RailPageShellCard className={`analysisRecommendation analysisRecommendation--${recommendation.priority.toLowerCase()}`} key={recommendation.id}>
          <span>{recommendation.priority}</span>
          <strong>{recommendation.title}</strong>
          <p>{recommendation.reason}</p>
          <Button variant="tertiary" size="sm" trailingIcon={<OpenInNewRoundedIcon />} onClick={() => onNavigate(recommendation.drillTarget)}>
            {DRILL_COPY[recommendation.drillTarget]}
          </Button>
        </RailPageShellCard>
      ))}
    </div>
  </div>
)
